import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  API_QR_CODE_RECEIPT_CONFIGURATIONS,
  API_GET_CONFIGURABLE_MERCHANTS_ID,
} from "../../utils/apis";
import {
  createEditAction,
  createViewAction,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_QR_CODE_RECEIPT_CONFIGURATIONS } from "../../utils/urls";
import { IFormSection, TFormHookParams } from "../../types/common";
import { VALIDATION_PRESETS } from "../../utils/formUtils";
import useGet from "../common/useGet";

const url = URL_QR_CODE_RECEIPT_CONFIGURATIONS;

export const useQRcodeReceiptConfigurations = () => {
  const tableColumns = () => [
    {
      id: "merchant_name",
      header: "Merchant",
      filter: {},
    },
    {
      id: "address",
      header: "Address",
      filter: {},
    },
    {
      id: "water_mark",
      header: "Water Mark",
      filter: {},
    },
    {
      id: "whatsapp",
      header: "WhatsApp",
      filter: {},
    },
    {
      id: "email",
      header: "Email",
      filter: {},
    },
    {
      id: "return_policy",
      header: "Return Policy",
      filter: {},
    },
    {
      id: "actions",
      header: "Actions",
    },
  ];

  const tableData = (data: any) => [
    ...(data?.map((item: any) => ({
      ...item,
      merchant_name:
        item?.merchant?.business_name ||
        item?.merchant?.username ||
        item?.merchant_name,
      actions: (
        <FormActions
          actions={[
            createViewAction(url, item?.id),
            createEditAction(url, item?.id),
          ]}
        />
      ),
    })) ?? []),
  ];

  const viewData = (item: any) => {
    const data = item?.results || item;

    return {
      sections: [
        {
          title: "QR Code Receipt Configuration Details",
          fields: {
            merchant:
              data?.merchant?.business_name ||
              data?.merchant?.username ||
              data?.merchant,
            address: data?.address,
            water_mark: data?.water_mark,
            whatsapp: data?.whatsapp,
            email: data?.email,
            return_policy: data?.return_policy,
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);
  const tableTitle = "QR Code Receipt Configurations";

  return {
    tableData,
    tableColumns,
    formRoute,
    viewData,
    tableTitle,
  };
};

export const useQRcodeReceiptConfigurationsForm = ({
  form,
}: TFormHookParams) => {
  const {
    data: merchantIDs,
    loading: merchantIDsLoading,
    loadData: merchantIDsLoadData,
  } = useGet();

  useEffect(() => {
    merchantIDsLoadData({ api: API_GET_CONFIGURABLE_MERCHANTS_ID });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "QR Code Receipt Configuration Details",
      fields: [
        {
          field_name: "merchant",
          type: "select",
          label: "Merchant",
          loading: merchantIDsLoading,
          description:
            "Select the merchant whose QR code receipts will use this configuration.",
          props: {
            required: true,
            value: form?.merchant,
            placeholder: "Select Merchant",
          },
          options_data: merchantIDs?.map((item: any) => ({
            id: item?.id,
            name: item?.name,
          })),
        },
        {
          field_name: "address",
          type: "text",
          label: "Address",
          description:
            "Enter the address to display on the merchant's receipt.",
          props: {
            required: true,
            placeholder: "Enter Address",
          },
        },
        {
          field_name: "water_mark",
          type: "text",
          label: "Water Mark",
          description: "Enter the watermark text to display on the receipt.",
          props: {
            required: true,
            placeholder: "Enter Water Mark",
          },
        },
        {
          field_name: "whatsapp",
          type: "tel",
          label: "WhatsApp",
          description:
            "Enter the WhatsApp number customers can use to contact the merchant.",
          props: {
            required: true,
            placeholder: "Enter WhatsApp Number",
          },
          validation: VALIDATION_PRESETS.PHONE_UG,
        },
        {
          field_name: "email",
          type: "email",
          label: "Email",
          description:
            "Enter the email address to display on the merchant's receipt.",
          props: {
            required: true,
            placeholder: "Enter Email Address",
          },
        },
        {
          field_name: "return_policy",
          type: "textarea",
          label: "Return Policy",
          description:
            "Enter the return policy to display on the merchant's receipt.",
          props: {
            required: true,
            placeholder: "Enter Return Policy",
          },
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  function onSubmit(payload: any, id: string | number) {
    return {
      id,
      api: API_QR_CODE_RECEIPT_CONFIGURATIONS,
      params: {
        ...payload,
        merchant:
          typeof payload.merchant === "object"
            ? payload.merchant.id
            : payload.merchant,
      },
    };
  }

  return { sections, redirect, onSubmit };
};
